import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Package, Clock, Check, XCircle, Copy } from "lucide-react";
import { toast } from "sonner";

const statusStyle = {
  paid: { label: "Confirmed", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20", Icon: Check },
  delivered: { label: "Delivered", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20", Icon: Check },
  awaiting_payment: { label: "Awaiting payment", cls: "bg-yellow-500/15 text-yellow-400 border-yellow-500/20", Icon: Clock },
  verifying: { label: "Verifying", cls: "bg-blue-500/15 text-blue-400 border-blue-500/20", Icon: Clock },
  pending: { label: "Processing", cls: "bg-yellow-500/15 text-yellow-400 border-yellow-500/20", Icon: Clock },
  failed: { label: "Failed", cls: "bg-red-500/15 text-red-400 border-red-500/20", Icon: XCircle },
  cancelled: { label: "Cancelled", cls: "bg-white/5 text-kyro-mute border-white/10", Icon: XCircle },
};

export default function AccountPage() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    api.get("/orders").then((r) => setOrders(r.data)).finally(() => setLoadingOrders(false));
  }, [user, loading, nav]);

  if (loading || !user) return <div className="min-h-[60vh] grid place-items-center text-kyro-mute">Loading...</div>;

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-14">
      <div className="flex items-start justify-between mb-10 gap-4 flex-wrap">
        <div>
          <div className="kyro-overline">My account</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-3">Hi, {user.name || user.email.split("@")[0]}</h1>
          <p className="text-kyro-mute mt-2">Manage your subscriptions, receipts, and access credentials.</p>
        </div>
        {user.role === "admin" && (
          <Link to="/admincontrol" data-testid="go-to-admin-btn" className="kyro-btn-secondary text-sm">Admin panel</Link>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-10">
        <div className="kyro-card p-6">
          <div className="kyro-overline">Email</div>
          <p className="mt-2 font-semibold truncate">{user.email}</p>
        </div>
        <div className="kyro-card p-6">
          <div className="kyro-overline">Orders</div>
          <p className="mt-2 font-display font-black text-2xl">{orders.length}</p>
        </div>
        <div className="kyro-card p-6">
          <div className="kyro-overline">Member since</div>
          <p className="mt-2 font-semibold text-sm">{user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}</p>
        </div>
      </div>

      <h2 className="font-display font-bold text-2xl mb-6">Recent orders</h2>
      {loadingOrders ? (
        <p className="text-kyro-mute">Loading orders...</p>
      ) : orders.length === 0 ? (
        <div className="kyro-card p-12 text-center">
          <Package className="w-8 h-8 mx-auto text-kyro-mute mb-3" />
          <p className="text-kyro-mute mb-6">No orders yet.</p>
          <Link to="/shop" className="kyro-btn-primary text-sm">Browse subscriptions</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => {
            const s = statusStyle[o.status] || statusStyle.pending;
            const S = s.Icon;
            return (
              <div key={o.id} data-testid={`order-row-${o.id}`} className="kyro-card p-5 md:p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-kyro-mute">Order</span>
                      <button onClick={() => { navigator.clipboard.writeText(o.id); toast.success("Order ID copied"); }} className="text-xs font-mono text-kyro-ink hover:text-[#FF4433] inline-flex items-center gap-1">{o.id} <Copy className="w-3 h-3" /></button>
                    </div>
                    <p className="text-xs text-kyro-faint mt-1">{new Date(o.created_at).toLocaleString()}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border ${s.cls}`}>
                    <S className="w-3 h-3" /> {s.label}
                  </span>
                </div>
                <div className="mt-5 space-y-2">
                  {o.items.map((it, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-md overflow-hidden bg-white/5 shrink-0">
                        {it.product_image && <img src={it.product_image} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{it.product_name}</p>
                        <p className="text-xs text-kyro-mute">{it.plan_label}</p>
                      </div>
                      <p className="text-sm font-semibold">₹{it.price.toFixed(0)}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex items-center justify-between pt-4 border-t border-white/5">
                  <p className="text-sm text-kyro-mute">Total</p>
                  <p className="font-display font-black">₹{o.total.toFixed(0)}</p>
                </div>
                {(o.status === "awaiting_payment" || o.status === "verifying") && (
                  <div className="mt-4">
                    <Link to={`/order/${o.id}?email=${encodeURIComponent(user.email)}`} className="kyro-btn-primary text-xs inline-flex items-center gap-1" data-testid={`resume-order-${o.id}`}>
                      {o.status === "awaiting_payment" ? "Complete payment" : "View verification status"}
                    </Link>
                  </div>
                )}
                {o.delivery_notes && o.status === "paid" && (
                  <div className="mt-4 p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-400 mb-2">Delivery details</div>
                    <p className="text-sm whitespace-pre-wrap">{o.delivery_notes}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-10">
        <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }} className="text-sm text-kyro-mute hover:text-[#FF4433]">Sign out</button>
      </div>
    </div>
  );
}
